import compact from 'lodash/compact'
import React, { useCallback } from 'react'
import styled from 'styled-components'

import { Checkbox, CheckboxGroup, FormOnChange, Loader, Tooltip } from '@procsea/design-system'

import { useLabels } from './useLabels'

const StyledImg = styled.img`
  height: 22px;
`

const StyledCheckbox = styled(Checkbox)`
  && label {
    padding: ${props => props.theme.spacings.small};
  }
`

export interface LabelSelectorProps {
  'data-e2e'?: string
  errorMessage?: string
  inlined?: boolean
  name?: string
  noWrap?: boolean
  onChange?: FormOnChange<Id[]>
  required?: boolean
  value?: Id[]
  warningMessage?: string
}

const LabelSelector = ({
  'data-e2e': dataE2e = 'label-radio-group',
  errorMessage,
  inlined,
  name,
  noWrap = true,
  onChange,
  required,
  value,
  warningMessage,
}: LabelSelectorProps) => {
  const { labels, isInitialLoading } = useLabels()

  const selectedLabelNames: string[] = value
    ? labels?.filter(({ id }) => value?.includes(id)).map(label => label.name) || []
    : []

  const handleChange: FormOnChange<string[]> = useCallback(
    ({ value: newValues }) => {
      const selectedLabelIds: Id[] = compact(
        labels?.filter(label => newValues.includes(label.name)).map(({ id }) => id)
      )

      onChange?.({ value: selectedLabelIds })
    },
    [onChange, JSON.stringify(labels)]
  )

  if (isInitialLoading) {
    return <Loader data-e2e="label-selector-loader" />
  }

  return !!labels?.length || required ? (
    <CheckboxGroup
      data-e2e={dataE2e}
      errorMessage={errorMessage}
      format="toggled"
      inlined={inlined}
      label={gettext('Label')}
      name={name}
      noWrap={noWrap}
      onChange={handleChange}
      required={required}
      value={selectedLabelNames}
      warningMessage={warningMessage}
    >
      {labels?.map(label => (
        // @TODO: Bring back tooltips when CheckboxGroup handles Checkboxes via context.
        <StyledCheckbox
          data-e2e="label-checkbox"
          content={
            <Tooltip placement="top" tooltip={label.name} trigger="hover" variant="black">
              <StyledImg alt={label.name} src={label.logo} data-e2e="label" />
            </Tooltip>
          }
          key={label.id}
          name={label.name}
          value={selectedLabelNames.includes(label.name)}
        />
      ))}
    </CheckboxGroup>
  ) : null
}

export default LabelSelector
